import { Info } from "lucide-react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import {
  RESULT_SCHEDULING_OPTIONS,
  TOOL_BEHAVIOR_OPTIONS,
  toolOptionsState,
  type LiveRuntime,
  type ToolOptions,
} from "@/utils/tool-options";

interface ToolOptionsFieldsProps {
  /** Unique per dialog: the radios' ids and the note's id derive from it. */
  idPrefix: string;
  runtime: LiveRuntime;
  /** The tool's name: one named like a platform tool takes no options (the bot drops them). */
  toolName?: string;
  value: ToolOptions;
  onChange: (value: ToolOptions) => void;
}

interface OptionRadiosProps<T extends string> {
  id: string;
  testId: string;
  options: ReadonlyArray<{ value: T; label: string; help: string }>;
  value: T;
  onChange: (value: T) => void;
  disabled: boolean;
}

const OptionRadios = <T extends string>({ id, testId, options, value, onChange, disabled }: OptionRadiosProps<T>) => (
  <RadioGroup value={value} onValueChange={(v) => onChange(v as T)} disabled={disabled} className="gap-3">
    {options.map((option) => {
      const optionId = `${id}-${option.value}`;
      return (
        <div key={option.value} className="flex items-start gap-2">
          <RadioGroupItem
            value={option.value}
            id={optionId}
            aria-describedby={`${optionId}-help`}
            data-testid={`${testId}-${option.value.replace(/_/g, "-")}`}
            className="peer mt-0.5"
          />
          <div className="grid gap-1 peer-disabled:opacity-70">
            <Label htmlFor={optionId} className={cn("font-normal", disabled ? "cursor-not-allowed" : "cursor-pointer")}>
              {option.label}
            </Label>
            <p id={`${optionId}-help`} className="text-xs text-muted-foreground">
              {option.help}
            </p>
          </div>
        </div>
      );
    })}
  </RadioGroup>
);

/**
 * A custom tool's tool-calling options. They act only on Gemini 3.8 Live; on
 * a text model the stored values are shown, disabled, with the reason, and kept
 * on save.
 */
export const ToolOptionsFields = ({ idPrefix, runtime, toolName, value, onChange }: ToolOptionsFieldsProps) => {
  const { locked: disabled, note } = toolOptionsState(runtime, toolName);
  const noteId = `${idPrefix}-tool-options-note`;

  return (
    <div className="space-y-4 rounded-lg border p-4" data-testid="tool-options" data-runtime={runtime}>
      <fieldset className="space-y-3" aria-describedby={noteId}>
        <legend className="text-sm font-medium">While this tool runs</legend>
        <p
          id={noteId}
          data-testid="tool-options-note"
          className={cn("flex gap-2 text-xs", disabled ? "rounded-md bg-muted/50 px-3 py-2" : "text-muted-foreground")}
        >
          {disabled && <Info className="h-4 w-4 shrink-0" aria-hidden="true" />}
          {note}
        </p>
        <OptionRadios
          id={`${idPrefix}-tool-behavior`}
          testId="tool-behavior"
          options={TOOL_BEHAVIOR_OPTIONS}
          value={value.toolBehavior}
          onChange={(toolBehavior) => onChange({ ...value, toolBehavior })}
          disabled={disabled}
        />
      </fieldset>

      {/* Only for Keep talking; the choice is kept while hidden. */}
      {value.toolBehavior === "non_blocking" && (
        <fieldset className="space-y-3" aria-describedby={noteId} data-testid="tool-scheduling">
          <legend className="text-sm font-medium">When the result arrives</legend>
          <OptionRadios
            id={`${idPrefix}-tool-scheduling`}
            testId="tool-scheduling"
            options={RESULT_SCHEDULING_OPTIONS}
            value={value.resultScheduling}
            onChange={(resultScheduling) => onChange({ ...value, resultScheduling })}
            disabled={disabled}
          />
        </fieldset>
      )}
    </div>
  );
};
